import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"

type SummaryVariant = "default" | "warning" | "success" | "danger"

interface SummaryCardProps {
  title: string
  primary: string
  secondary?: string | undefined
  variant?: SummaryVariant
  href: string
  icon?: React.ReactNode
}

const variantStyles: Record<SummaryVariant, string> = {
  default: "border-border bg-card hover:border-primary/40",
  warning: "border-amber-200 bg-amber-50/30 hover:border-amber-400",
  success: "border-green-200 bg-green-50/30 hover:border-green-400",
  danger: "border-red-200 bg-red-50/30 hover:border-red-400",
}

const secondaryStyles: Record<SummaryVariant, string> = {
  default: "text-muted-foreground",
  warning: "text-amber-600",
  success: "text-green-600",
  danger: "text-destructive",
}

export function SummaryCard({
  title,
  primary,
  secondary,
  variant = "default",
  href,
  icon,
}: SummaryCardProps) {
  return (
    <Link
      to={href}
      className={cn(
        "block p-3 rounded-lg border transition-colors group",
        variantStyles[variant]
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {icon && <span className="text-muted-foreground">{icon}</span>}
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <p className="text-lg font-semibold mt-1">{primary}</p>
      {secondary && (
        <p className={cn("text-xs mt-0.5", secondaryStyles[variant])}>
          {secondary}
        </p>
      )}
    </Link>
  )
}

interface SummaryCardsGridProps {
  children: React.ReactNode
}

export function SummaryCardsGrid({ children }: SummaryCardsGridProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {children}
    </div>
  )
}
